import { useEffect, useRef, useState } from "react";
import { Button } from "@opencloud/ui";
import { Check, Copy, LoaderCircle } from "lucide-react";

type CopyState = "idle" | "copying" | "copied" | "error";

export function CopyableCode({ label, action, value }: {
  readonly label: string;
  readonly action: string;
  readonly value: string;
}) {
  const [state, setState] = useState<CopyState>("idle");
  const currentValue = useRef(value);
  const request = useRef(0);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => {
    currentValue.current = value;
    request.current += 1;
    setState("idle");
    clearTimeout(timer.current);
    return () => {
      request.current += 1;
      clearTimeout(timer.current);
    };
  }, [value]);

  async function copy() {
    const requestId = ++request.current;
    const copiedValue = value;
    clearTimeout(timer.current);
    setState("copying");
    try {
      if (!navigator.clipboard?.writeText) throw new Error("Clipboard unavailable");
      await navigator.clipboard.writeText(copiedValue);
      if (request.current !== requestId || currentValue.current !== copiedValue) return;
      setState("copied");
      timer.current = setTimeout(() => setState("idle"), 2500);
    } catch {
      if (request.current === requestId && currentValue.current === copiedValue) setState("error");
    }
  }

  return <section className="a3-code-block" aria-label={`${label} for ${action}`} data-copy-state={state}>
    <div className="a3-code-heading"><span>{label}</span>
      <span className={state === "error" ? "a3-copy-feedback" : "a3-copy-feedback sr-only"} role="status">{state === "copied" ? `${label} copied` : state === "error" ? "Couldn’t copy" : state === "copying" ? "Copying…" : ""}</span>
    </div>
    <div className="a3-code-payload">
      <pre tabIndex={0} aria-label={`${label} text for ${action}`}>{value}</pre>
      <Button type="button" variant="ghost" size="icon" className="a3-code-copy" disabled={state === "copying"}
        aria-label={`Copy ${label.toLowerCase()} for ${action}`} title={`Copy ${label.toLowerCase()}`}
        onClick={() => { void copy(); }}>
        {state === "copied" ? <Check aria-hidden="true" /> : state === "copying" ? <LoaderCircle className="a3-spin" aria-hidden="true" /> : <Copy aria-hidden="true" />}
      </Button>
    </div>
    {state === "error" ? <p className="a3-copy-error">Select and copy the text, or try again.</p> : null}
  </section>;
}
